import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authService } from "../services/auth.service";
import type { User } from "@leave-system/shared/types";
import type { LoginCredentials, RegisterData } from "../services/auth.service";
import { useState } from "react";

const AUTH_STORAGE_KEY = "leave-system-auth";

export function useAuth() {
  const queryClient = useQueryClient();
  const [shouldFetchUser, setShouldFetchUser] = useState(() => {
    // Check if user might be authenticated based on localStorage
    try {
      return localStorage.getItem(AUTH_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Get current user - only enabled if shouldFetchUser is true
  const {
    data: user,
    isLoading,
    isError,
    error,
    refetch: refetchUser,
  } = useQuery<User | null>({
    queryKey: ["auth", "user"],
    queryFn: async () => {
      const user = await authService.getCurrentUser();
      // Update localStorage based on result
      if (user) {
        localStorage.setItem(AUTH_STORAGE_KEY, "true");
      } else {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      }
      return user;
    },
    enabled: shouldFetchUser,
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) =>
      authService.login(credentials),
    onSuccess: (data) => {
      queryClient.setQueryData(["auth", "user"], data.user);
      localStorage.setItem(AUTH_STORAGE_KEY, "true");
      setShouldFetchUser(true);
      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["auth", "user"] });
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (data: RegisterData) => authService.register(data),
    onSuccess: (data) => {
      queryClient.setQueryData(["auth", "user"], data.user);
      localStorage.setItem(AUTH_STORAGE_KEY, "true");
      setShouldFetchUser(true);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      // Clear all auth state
      queryClient.setQueryData(["auth", "user"], null);
      localStorage.clear();
      sessionStorage.clear();
      setShouldFetchUser(false);

      // Clear all cookies
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
      });

      // Clear React Query cache
      queryClient.clear();

      // Force reload to login page
      window.location.replace("/login");
    },
    onError: () => {
      // Even if logout API fails, clear local state
      queryClient.setQueryData(["auth", "user"], null);
      localStorage.clear();
      sessionStorage.clear();
      queryClient.clear();
      window.location.replace("/login");
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => authService.changePassword(currentPassword, newPassword),
  });

  // Request password reset mutation
  const requestPasswordResetMutation = useMutation({
    mutationFn: (email: string) => authService.requestPasswordReset(email),
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: ({
      token,
      newPassword,
    }: {
      token: string;
      newPassword: string;
    }) => authService.resetPassword(token, newPassword),
  });

  return {
    // User data
    user,
    isAuthenticated: !!user,
    isLoading,
    isError,
    error,
    refetchUser,

    // Login
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    isLoginLoading: loginMutation.isPending,
    loginError: loginMutation.error,

    // Register
    register: registerMutation.mutate,
    registerAsync: registerMutation.mutateAsync,
    isRegisterLoading: registerMutation.isPending,
    registerError: registerMutation.error,

    // Logout
    logout: logoutMutation.mutate,
    logoutAsync: logoutMutation.mutateAsync,
    isLogoutLoading: logoutMutation.isPending,

    // Password management
    changePassword: changePasswordMutation.mutate,
    changePasswordAsync: changePasswordMutation.mutateAsync,
    isChangePasswordLoading: changePasswordMutation.isPending,
    changePasswordError: changePasswordMutation.error,

    requestPasswordReset: requestPasswordResetMutation.mutate,
    requestPasswordResetAsync: requestPasswordResetMutation.mutateAsync,
    isRequestPasswordResetLoading: requestPasswordResetMutation.isPending,

    resetPassword: resetPasswordMutation.mutate,
    resetPasswordAsync: resetPasswordMutation.mutateAsync,
    isResetPasswordLoading: resetPasswordMutation.isPending,
    resetPasswordError: resetPasswordMutation.error,
  };
}

export default useAuth;
